import { Box, Row, Column, H2, GridContainer, BoundedContent, FlexBox, Link, Hidden } from '@vp/swan'
import CarouselTilesList from './CarouselTilesList'
import { createOnClickTrackingHandler } from '../../utils/trackingUtils'
import RowTilesList from './RowTilesList'
import { useTrackingContext } from '../../hooks/trackingContext'
import { getTrackingConfig, getTrackingConfigLinkAttributes } from '../../utils/trackingConfig'
import { useLogger, useUserContext } from '@vp/ubik-context'
import GridTilesList from './GridTilesList'
import type { TilesSectionProps } from '../../types/homepageFeatureTileRow'
import { FEATURE_TILE_SCHEMA } from '../../utils/trackingSchemas'

const defaultCarouselResponsiveSettings = [
  {
    breakpoint: 768,
    settings: {
      slidesToShow: 2.15,
      slidesToScroll: 2,
    },
  },
]

const TilesSection = (props: TilesSectionProps) => {
  const { tileData, tileImageLoadingStrategy, tileTrackingZone, id, tileRow, useNewTiles, enableCampaignCallouts, gridToggle, carouselA11yText, locale } = props

  const { sectionHeader, tileList, tileSkin, callToAction, tileLayout } = tileData

  const logger = useLogger()

  if (!tileList?.length) {
    return <></>
  }

  const userContext = useUserContext()

  const customZone = tileRow ? `${tileTrackingZone}_${sectionHeader}_Row${tileRow}` : tileTrackingZone

  const { sourcePagePath } = useTrackingContext()

  const isCtaUrlDefined = callToAction && callToAction[0] && callToAction[0].targetUrl
  const ctaUrl = isCtaUrlDefined ? callToAction[0].targetUrl.contentfulUrl : ''
  const ctaCopy = callToAction && callToAction[0] && callToAction[0].text
  const trackingData = {
    ctaCopy,
    sourcePagePath,
    ctaUrl,
    sectionHeader,
    row: tileRow,
    section: tileTrackingZone,
  }

  const visibleTiles = tileLayout || 6

  const asCarousel = tileList.length > visibleTiles

  if (userContext.experiments && userContext.experiments.homepage_cluster_tile_component === 'test' && ['1', '2', '4'].includes(tileRow)) {
    return null
  }

  const CarouselTiles = () => (
    <CarouselTilesList
      loadType={tileImageLoadingStrategy}
      useNewTiles={useNewTiles}
      enableCampaignCallouts={enableCampaignCallouts}
      tiles={tileList}
      trackingPageZone={customZone}
      id={id}
      carouselResponsiveSettings={defaultCarouselResponsiveSettings}
      tileSkin={tileSkin}
      visibleTiles={visibleTiles}
      tileRow={tileRow}
      carouselA11yText={carouselA11yText}
      sectionHeader={sectionHeader}
      locale={locale}
    />
  )

  return (
    <BoundedContent mt="0" mb="9">
      <GridContainer>
        <Box mb="between-sections">
          {sectionHeader && (
            <Row>
              <Column
                span={12}
                onClick={isCtaUrlDefined && createOnClickTrackingHandler(getTrackingConfig(trackingData), logger, FEATURE_TILE_SCHEMA, ctaUrl)}
                {...getTrackingConfigLinkAttributes(trackingData)}
              >
                <FlexBox alignItems="baseline" flexWrap="wrap" mb="6">
                  {sectionHeader.trim().length > 0 && (
                    <H2 margin={0} fontSkin="title-section" mb="0">
                      {sectionHeader}
                    </H2>
                  )}
                  {isCtaUrlDefined && (
                    <Link ml="5" href={callToAction[0].targetUrl.contentfulUrl} skin="cta" style={{ whiteSpace: 'nowrap' }}>
                      {callToAction[0].text}
                    </Link>
                  )}
                </FlexBox>
              </Column>
            </Row>
          )}
          <Hidden md lg xl>
            {gridToggle ? (
              <GridTilesList
                loadType={tileImageLoadingStrategy}
                useNewTiles={useNewTiles}
                enableCampaignCallouts={enableCampaignCallouts}
                tiles={tileList}
                trackingPageZone={customZone}
                id={id}
                tileSkin={tileSkin}
                locale={locale}
              />
            ) : (
              <CarouselTiles />
            )}
          </Hidden>
          <Hidden xs sm>
            {asCarousel ? (
              <CarouselTiles />
            ) : (
              <RowTilesList
                loadType={tileImageLoadingStrategy}
                useNewTiles={useNewTiles}
                enableCampaignCallouts={enableCampaignCallouts}
                tiles={tileList}
                trackingPageZone={customZone}
                id={id}
                carouselResponsiveSettings={defaultCarouselResponsiveSettings}
                tileSkin={tileSkin}
                visibleTiles={visibleTiles}
                locale={locale}
              />
            )}
          </Hidden>
        </Box>
      </GridContainer>
    </BoundedContent>
  )
}

export default TilesSection
